import { useEffect, useRef, useCallback } from 'react';
import { ChatMessageResponse } from '@/lib/api';
import { MessageItem } from './MessageItem';

interface MessageListProps {
  messages: ChatMessageResponse[];
  currentUserId: string | null;
  isAdmin?: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onStartEditMessage?: (messageId: string) => void;
  onDeleteMessage?: (messageId: string) => void;
  onReplyMessage?: (messageId: string) => void;
  onReactionMessage?: (messageId: string, emoji: string) => void;
  onOpenDm?: (userId: string) => void;
}

export function MessageList({
  messages,
  currentUserId,
  isAdmin,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onStartEditMessage,
  onDeleteMessage,
  onReplyMessage,
  onReactionMessage,
  onOpenDm,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);

  // Scroll to bottom on initial load and when new messages arrive
  useEffect(() => {
    if (!loading && messages.length > 0) {
      if (prevMessageCountRef.current === 0) {
        // Initial load — instant scroll
        bottomRef.current?.scrollIntoView();
      } else if (messages.length > prevMessageCountRef.current) {
        // New message arrived — smooth scroll
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
      prevMessageCountRef.current = messages.length;
    }
    // Reset when loading starts (channel switch)
    if (loading) {
      prevMessageCountRef.current = 0;
    }
  }, [loading, messages.length]);

  // Load more on scroll to top
  const handleScroll = useCallback(() => {
    if (!containerRef.current || !hasMore || loadingMore) return;
    if (containerRef.current.scrollTop < 100) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, onLoadMore]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-lg px-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="h-8 w-8 bg-muted rounded-full" />
              <div className="flex-1">
                <div className="h-3 bg-muted rounded w-20 mb-2" />
                <div className="h-4 bg-muted rounded w-48" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No messages yet</p>
          <p className="text-sm mt-1">Start the conversation!</p>
        </div>
      </div>
    );
  }

  let lastDate = '';

  return (
    <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-0 py-2">
      {loadingMore && (
        <div className="flex justify-center py-2">
          <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {messages.map((message, index) => {
        const messageDate = new Date(message.createdAt).toLocaleDateString();
        const showDateSeparator = messageDate !== lastDate;
        lastDate = messageDate;

        const prev = index > 0 ? messages[index - 1] : null;
        const next = index < messages.length - 1 ? messages[index + 1] : null;

        const isGroupMember = (
          a: (typeof messages)[0],
          b: (typeof messages)[0],
          sameDate: boolean,
        ) =>
          sameDate &&
          !a.isDeleted &&
          !b.isDeleted &&
          a.messageType !== 'system' &&
          b.messageType !== 'system' &&
          a.senderId === b.senderId;

        const isConsecutive =
          prev !== null && !showDateSeparator && isGroupMember(prev, message, true);

        const nextDate = next ? new Date(next.createdAt).toLocaleDateString() : null;
        const isLastInGroup = !next || !isGroupMember(message, next, nextDate === messageDate);

        return (
          <div key={message.messageId}>
            {showDateSeparator && (
              <div className="flex items-center gap-0 my-4">
                <div className="flex-1 h-px bg-primary/10" />
                <span className="text-[10px] text-primary font-medium bg-background border border-primary/10 py-2 px-3 rounded-full">
                  {new Date(message.createdAt).toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
                <div className="flex-1 h-px bg-primary/10" />
              </div>
            )}
            <MessageItem
              message={message}
              isOwnMessage={message.senderId === currentUserId}
              isAdmin={isAdmin}
              isConsecutive={isConsecutive}
              isLastInGroup={isLastInGroup}
              onStartEdit={onStartEditMessage}
              onDelete={onDeleteMessage}
              onReply={onReplyMessage}
              onReaction={onReactionMessage}
              onOpenDm={onOpenDm}
            />
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
